import { Component, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SelectSearchModule } from 'src/components/select-search/select-search.module';
import { LocationObject } from '@tradecafe/types/core';
import { LocationsService } from 'src/pages/admin/settings/locations/locations.service';
import { ToasterService } from 'src/shared/toaster/toaster.service';

export interface LocationSelectFormData {
  locations: string[];
  options?: {
    title?: string;
    actionTitle?: string;
    multiple?: boolean;
  };
}

@Component({
  standalone: true,
  selector: 'tc-location-select-form',
  templateUrl: './location-select-form.component.html',
  styleUrls: ['./location-select-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    SelectSearchModule,
  ]
})
export class LocationSelectFormComponent implements OnInit {
  private dialogRef = inject<MatDialogRef<LocationSelectFormComponent>>(MatDialogRef)
  data = inject<LocationSelectFormData>(MAT_DIALOG_DATA)
  private locationsService = inject(LocationsService)
  private toaster = inject(ToasterService)

  locationControl = new UntypedFormControl(null, Validators.required);
  locations: Record<string, LocationObject> = {};
  locationsArray: LocationObject[] = [];
  isLoading = false;
  isProcessing = false;

  title = 'Select Address';
  actionTitle = 'Update';
  multiple = false;

  async ngOnInit(): Promise<void> {
    // Set options from data
    if (this.data?.options) {
      this.title = this.data.options.title || this.title;
      this.actionTitle = this.data.options.actionTitle || this.actionTitle;
      this.multiple = this.data.options.multiple || false;
    }

    // Load locations
    await this.loadLocations();
  }

  async loadLocations(): Promise<void> {
    this.isLoading = true;

    try {
      this.locations = await this.locationsService.getLocationsByIds(this.data.locations);
      this.locationsArray = Object.values(this.locations).sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } catch (err) {
      console.error('Unable to load location data', err);
      this.toaster.error('Unable to load location data', err);
    } finally {
      this.isLoading = false;
    }
  }

  save(): void {
    if (this.locationControl.invalid || this.isProcessing) {
      return;
    }

    const locationId = this.locationControl.value;

    if (this.multiple) {
      this.dialogRef.close(locationId);
    } else {
      this.dialogRef.close(this.locations[locationId]);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}